import { useState } from 'react';
import { Box, Button, Grid, Link, Typography } from '@mui/material';
import isEmail from 'validator/es/lib/isEmail';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import TextField from '../textField';
import { useUserContext } from '@/contexts/userContext';
import ErrorMessage from '../errorMessage';
import { useNavigate } from 'react-router-dom';
import { validatePassword } from '@/utils';

type Props = {};

const SignUpForm = ({}: Props) => {
  const { signUp } = useUserContext();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [apiError, setApiError] = useState<any>();

  const handleSubmit = async () => {
    setApiError(null);
    const errors: any = {};
    let hasError = false;

    if (!executeRecaptcha) {
      setApiError('ReCAPTCHA not ready. Please try again.');
      return;
    }

    try {
      if (!firstName) {
        errors['firstName'] = 'Required';
        hasError = true;
      }
      if (!lastName) {
        errors['lastName'] = 'Required';
        hasError = true;
      }
      if (!email) {
        errors['email'] = 'Required';
        hasError = true;
      }
      if (!isEmail(email)) {
        errors['email'] = 'Please enter valid email';
        hasError = true;
      }
      if (!password) {
        errors['password'] = 'Required';
        hasError = true;
      }
      if (!validatePassword(password)) {
        errors['password'] = 'Should be 8 to 100 chars long';
        hasError = true;
      }
      if (!confirmPassword) {
        errors['confirmPassword'] = 'Required';
        hasError = true;
      }
      if (password !== confirmPassword) {
        errors['confirmPassword'] = 'Passwords must be same';
        hasError = true;
      }

      if (!hasError) {
        setLoading(true);
        const recaptchaToken = await executeRecaptcha('signup');
        await signUp({ recaptchaToken, firstName, lastName, email, password });
        navigate('/login');
      }
    } catch (error: any) {
      setApiError(error);
    }
    setLoading(false);
    setErrors(errors);
  };

  const handleSignIn = () => navigate('/login');
  const handleCancel = () => navigate('/');

  return (
    <Box
      sx={{ border: 1, borderColor: 'divider', borderRadius: '12px', overflow: 'hidden', bgcolor: 'background.paper' }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Create an account
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Fill in the details below to get started
        </Typography>
        {apiError && (
          <Box sx={{ mt: 1 }}>
            <ErrorMessage error={apiError} />
          </Box>
        )}
      </Box>

      {/* Fields */}
      <Box sx={{ px: 3, py: 2.5 }}>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              value={firstName}
              label={'First Name'}
              required
              error={errors['firstName']}
              onChange={setFirstName}
              autoFocus
              maxLength={100}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              value={lastName}
              label={'Last Name'}
              required
              error={errors['lastName']}
              onChange={setLastName}
              maxLength={100}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              value={email}
              label={'Email Address'}
              required
              error={errors['email']}
              onChange={setEmail}
              type="email"
              maxLength={100}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              value={password}
              label={'Password'}
              required
              error={errors['password']}
              onChange={setPassword}
              type="password"
              maxLength={100}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              value={confirmPassword}
              label={'Confirm Password'}
              required
              error={errors['confirmPassword']}
              onChange={setConfirmPassword}
              type="password"
              maxLength={100}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="caption" color="text.secondary">
              This site is protected by reCAPTCHA and the Google{' '}
              <Link href="https://policies.google.com/privacy" target="_blank">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link href="https://policies.google.com/terms" target="_blank">
                Terms of Service
              </Link>{' '}
              apply.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Action bar */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Link href="#" variant="body2" onClick={handleSignIn} sx={{ fontWeight: 600 }}>
          Already have an account? Sign in
        </Link>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="text" size="small" onClick={handleCancel} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            loading={loading}
            size="small"
            color="secondary"
            variant="contained"
            onClick={handleSubmit}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
              color: 'text.primary',
            }}
          >
            Sign Up
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SignUpForm;
